import { useCallback, useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";

/** 아직 표준 lib 에 없는 설치 프롬프트 이벤트. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let registrationPromise: Promise<ServiceWorkerRegistration | undefined> | null = null;

/**
 * 서비스워커를 **한 번만** 등록하고 registration 을 재사용한다.
 * FCM `getToken` 에 이 registration 을 넘겨야 우리가 만든 SW 가 푸시를 처리한다.
 *
 * 등록 콜백이 오지 않는 환경(비보안 오리진 등)에서도 호출부가 멈추지 않도록
 * 타임아웃을 두고 `undefined` 로 해결한다.
 */
export function ensureServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  registrationPromise ??= new Promise((resolve) => {
    if (!("serviceWorker" in navigator)) {
      resolve(undefined);
      return;
    }

    let settled = false;
    const settle = (registration?: ServiceWorkerRegistration) => {
      if (settled) return;
      settled = true;
      resolve(registration);
    };

    registerSW({
      immediate: true,
      onRegisteredSW: (_swUrl, registration) => settle(registration),
      onRegisterError: (error) => {
        console.warn("[pwa] 서비스워커 등록 실패:", error);
        settle(undefined);
      },
    });

    window.setTimeout(() => settle(undefined), 8_000);
  });

  return registrationPromise;
}

/** 네트워크 연결 상태(오프라인 배너 표시용). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  return online;
}

/**
 * 홈화면/데스크톱 설치 프롬프트.
 * iOS Safari 는 `beforeinstallprompt` 를 발생시키지 않으므로 `canInstall` 이 항상
 * false 다 — 그 경우 공유 시트 ▸ "홈 화면에 추가" 안내로 대체한다.
 */
export function useInstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // 브라우저 기본 안내를 막고 우리 버튼으로 유도
      setEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setEvent(null);

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!event) return;
    await event.prompt();
    setEvent(null); // 프롬프트는 한 번만 쓸 수 있다
  }, [event]);

  return { canInstall: event !== null, promptInstall };
}

/** 이미 설치된(standalone) 상태인지. */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari 전용 플래그
    (navigator as { standalone?: boolean }).standalone === true
  );
}
